import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, MapPin, Globe, ChevronDown, Check, LogIn, User as UserIcon, LayoutDashboard, LogOut } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useAuth, dashboardFor } from '../lib/auth';

const LANGS = [
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'zh', label: '中文', short: '中文' },
];

function useOutsideClose(ref, cb) {
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) cb(); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [ref, cb]);
}

function LanguageSwitcher() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1 rounded-md hover:bg-white/8 transition-colors text-white/85 hover:text-[#f5b300]"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-switcher-button"
      >
        <Globe className="w-3.5 h-3.5 text-[#f5b300]" />
        <span className="font-semibold tracking-wide">{current.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-40 rounded-md bg-white shadow-[0_14px_30px_-10px_rgba(0,0,0,0.4)] border border-neutral-100 overflow-hidden z-50">
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              data-testid={`lang-option-${l.code}`}
              className={`w-full flex items-center justify-between px-3.5 py-2.5 text-[12.5px] font-medium text-left hover:bg-[#fff7e0] transition-colors ${
                l.code === lang ? 'text-[#c89200]' : 'text-[#14110f]'
              }`}
            >
              {l.label}
              {l.code === lang && <Check className="w-3.5 h-3.5 text-[#f5b300]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AuthControl() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  if (!user) {
    return (
      <Link
        to="/login"
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-[#f5b300] text-[#14110f] hover:bg-[#e0a200] font-semibold text-[11px] uppercase tracking-wider transition"
      >
        <LogIn className="w-3.5 h-3.5" /> {t('top.signIn')}
      </Link>
    );
  }
  const first = (user.name || user.email).split(' ')[0];
  const initial = (user.name || user.email).trim().slice(0, 1).toUpperCase();
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/8 text-white/90 transition">
        <span className="w-6 h-6 rounded-full bg-[#f5b300] text-[#14110f] flex items-center justify-center text-[11px] font-bold">{initial}</span>
        <span className="font-semibold tracking-wide max-w-[120px] truncate">{first}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 rounded-md bg-white shadow-[0_14px_30px_-10px_rgba(0,0,0,0.4)] border border-neutral-100 overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-neutral-100">
            <p className="text-[13px] font-semibold text-[#14110f] truncate">{user.name}</p>
            <p className="text-[11.5px] text-neutral-500 truncate">{user.email}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#c89200] mt-1">{user.role}</p>
          </div>
          <button onClick={() => { setOpen(false); nav(dashboardFor(user.role)); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium text-[#14110f] hover:bg-[#fff7e0] transition">
            <LayoutDashboard className="w-3.5 h-3.5 text-[#c89200]" /> {t('top.dashboard')}
          </button>
          <button onClick={() => { setOpen(false); logout(); nav('/'); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium text-rose-600 hover:bg-rose-50 transition border-t border-neutral-100">
            <LogOut className="w-3.5 h-3.5" /> {t('top.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function TribeBanner() {
  const { t } = useLang();
  return (
    <div className="hidden lg:block bg-[#14110f] text-white/85 text-xs">
      <div className="max-w-[1280px] mx-auto px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <Link to="/" className="tracking-wide hover:text-[#f5b300] transition-colors">
            {t('top.home')}
          </Link>
          <span className="text-white/20">|</span>
          <Link to="/" className="tracking-wide text-[#f5b300] font-semibold">
            {t('top.wysetribe')}
          </Link>
        </div>
        <div className="flex items-center gap-5">
          <span className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-[#f5b300]" /> {t('top.locations')}
          </span>
          <a href="mailto:info@wysetribe.ca" className="flex items-center gap-1.5 hover:text-[#f5b300] transition-colors">
            <Mail className="w-3.5 h-3.5 text-[#f5b300]" /> info@wysetribe.ca
          </a>
          <span className="w-px h-4 bg-white/15" />
          <LanguageSwitcher />
          <span className="w-px h-4 bg-white/15" />
          <AuthControl />
        </div>
      </div>
    </div>
  );
}
